import { ApolloError } from '@apollo/client'
import React from 'react'

import { Spinner, Tag, Text } from '@babylon/core-ui'

import { isFilledArray } from '~/core'
import { useMessages } from '~/core/hooks'
import { AppointmentInvitesQueryResult } from '~/generated'

import TimelineFollowUpActions from './TimelineFollowUpActions'

import styles from './styles.module.scss'
import messages from './TimelineConsultationActionsView.messages'

export interface TimelineConsultationActionsViewProps {
  appointmentInvites: NonNullable<
    NonNullable<AppointmentInvitesQueryResult['data']>['consultation']
  >['appointmentInvites']
  isIncomplete: boolean
  loading: boolean
  error?: ApolloError
}

interface TimelineTimelineActionTagProps {
  children: React.ReactNode
}

const TimelineActionTag = ({ children }: TimelineTimelineActionTagProps) => (
  <Tag className={styles.clinicalCode}>{children}</Tag>
)

const TimelineConsultationActionsView = ({
  appointmentInvites,
  isIncomplete,
  loading,
  error,
}: TimelineConsultationActionsViewProps) => {
  const f = useMessages(messages)

  if (loading) {
    return (
      <Spinner size="small" testid="timeline-consultation-actions-loading" />
    )
  }

  if (error) {
    return <Text>{f('error_title')}</Text>
  }

  const hasAppointmentInvites = isFilledArray(appointmentInvites)

  if (!hasAppointmentInvites) return null

  if (isIncomplete) {
    return (
      <div
        data-testid="timeline-incomplete-consultation-actions"
        className={styles.incompleteConsultationActions}
      >
        <Text className={styles.possibleAction}>{f('possible_actions')}:</Text>
        <div className={styles.workflowGroup}>
          <Text className={styles.completeTimelineActionLabel}>
            {f('appointment_invites_label')}
          </Text>
          {appointmentInvites?.map(({ id, service_type: serviceType }) => (
            <TimelineActionTag key={id}>
              {serviceType?.name ?? f('no_service_type')}
            </TimelineActionTag>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div
      data-testid="timeline-complete-consultation-actions"
      className={styles.completeConsultationActions}
    >
      <TimelineFollowUpActions appointmentInvites={appointmentInvites} />
    </div>
  )
}

export default TimelineConsultationActionsView
